import { Box } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useLanguage } from '../GeneralUtils';
import { mb } from '../theme/mbTokens';

type RichTextEditorProps = {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  contentLanguage?: 'he' | 'en';
  minLines?: number;
};

export const RichTextEditor = ({ value, onChange, readOnly, contentLanguage, minLines }: RichTextEditorProps) => {
  const systemLanguage = useLanguage();
  const lang = contentLanguage || systemLanguage;

  const modules = useMemo(
    () => ({
      toolbar: [[{ header: [1, 2, false] }], ['bold', 'italic', 'underline', 'strike', 'blockquote'], ['link']],
      clipboard: {
        matchVisual: false,
        matchers: [
          [
            Node.ELEMENT_NODE,
            (_node: Node, delta: { ops: { insert?: string; attributes?: Record<string, unknown> }[] }) => {
              delta.ops = delta.ops.map((op) => {
                if (op.attributes) {
                  const { bold, italic, underline, link } = op.attributes;
                  op.attributes = {};
                  if (bold) op.attributes.bold = bold;
                  if (italic) op.attributes.italic = italic;
                  if (underline) op.attributes.underline = underline;
                  if (link) op.attributes.link = link;
                  if (Object.keys(op.attributes).length === 0) delete op.attributes;
                }
                return op;
              });
              return delta;
            },
          ],
        ],
      },
    }),
    [],
  );

  useEffect(() => {
    const styleId = 'dynamic-quill-link-styles';
    let styleElement = document.getElementById(styleId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }
    styleElement.innerHTML = `.ql-editor a { color: ${mb.green} !important; text-decoration: underline !important; }`;
  }, []);

  return (
    <Box
      sx={{
        '& .ql-editor': {
          direction: systemLanguage === 'he' ? (lang === 'he' ? 'ltr' : 'rtl') : lang === 'he' ? 'rtl' : 'ltr',
          textAlign: systemLanguage === 'he' ? (lang === 'he' ? 'left' : 'right') : lang === 'he' ? 'right' : 'left',
          ...(minLines && { minHeight: `${minLines * 1.5}em` }),
        },
      }}>
      <ReactQuill theme="snow" value={value} onChange={onChange} modules={modules} readOnly={readOnly} />
    </Box>
  );
};
